import React, { useState, useMemo } from 'react';
import styles from './MoveExplanationModal.module.css';
import { AIMoveResult } from '../../services/zodiacEngine/AI/Core/IAIPlayer';

interface MoveExplanationModalProps {
  moveResult: AIMoveResult;
  variantLabel: string;
  moveDescription: string;
  onClose: () => void;
}

// ── Priority log parser ──

interface PriorityStep {
  number: number;
  name: string;
  status: 'skipped' | 'relevant' | 'early_exit' | 'filtering' | 'no_action' | 'error';
  reasoning?: string;
  movesFiltered?: number;
  details: string[];
}

function parseDecisionLog(logs: string[]): {
  steps: PriorityStep[];
  fallbackType: 'priority' | 'minimax' | 'mcts' | null;
  preamble: string[];
  postamble: string[];
} {
  const steps: PriorityStep[] = [];
  const preamble: string[] = [];
  const postamble: string[] = [];
  let current: PriorityStep | null = null;
  let reachedPriorities = false;
  let passedPriorities = false;
  let fallbackType: 'priority' | 'minimax' | 'mcts' | null = null;

  for (const line of logs) {
    // Detect new priority execution
    const execMatch = line.match(/EXECUTING Priority (\d+): (.+)/);
    if (execMatch) {
      reachedPriorities = true;
      if (current) steps.push(current);
      current = {
        number: parseInt(execMatch[1]),
        name: execMatch[2],
        status: 'relevant',
        details: [],
      };
      continue;
    }

    // Detect skip
    const skipMatch = line.match(/Priority (\d+) \((.+?)\) not relevant/);
    if (skipMatch && current && current.number === parseInt(skipMatch[1])) {
      current.status = 'skipped';
      continue;
    }

    // Detect early exit
    const exitMatch = line.match(/PRIORITY (\d+) \((.+?)\) EARLY EXIT: (.+)/);
    if (exitMatch && current && current.number === parseInt(exitMatch[1])) {
      current.status = 'early_exit';
      current.reasoning = exitMatch[3];
      fallbackType = 'priority';
      continue;
    }

    // Detect filtering
    const filterMatch = line.match(/PRIORITY (\d+) \((.+?)\) FILTERING: (.+?) \((\d+) moves filtered\)/);
    if (filterMatch && current && current.number === parseInt(filterMatch[1])) {
      current.status = 'filtering';
      current.reasoning = filterMatch[3];
      current.movesFiltered = parseInt(filterMatch[4]);
      continue;
    }

    // Detect no action
    const noActionMatch = line.match(/Priority (\d+) \((.+?)\) took no action/);
    if (noActionMatch && current && current.number === parseInt(noActionMatch[1])) {
      current.status = 'no_action';
      continue;
    }

    // Detect error
    if (line.includes('CRITICAL ERROR') && current) {
      current.status = 'error';
      current.details.push(line);
      continue;
    }

    // Detect minimax fallback
    if (line.includes('Minimax fallback') || line.includes('Minimax selected')) {
      fallbackType = 'minimax';
      if (current) { steps.push(current); current = null; }
      passedPriorities = true;
      postamble.push(line);
      continue;
    }

    // Detect MCTS fallback
    if (line.includes('Focused MCTS') || line.includes('MCTS Search')) {
      fallbackType = 'mcts';
      if (current) { steps.push(current); current = null; }
      passedPriorities = true;
      postamble.push(line);
      continue;
    }

    // Collect detail lines
    if (current && reachedPriorities) {
      // Skip the "is relevant - analyzing" lines (redundant)
      if (line.includes('is relevant - analyzing')) continue;
      current.details.push(line);
    } else if (!reachedPriorities) {
      preamble.push(line);
    } else if (passedPriorities) {
      postamble.push(line);
    }
  }

  if (current) steps.push(current);
  return { steps, fallbackType, preamble, postamble };
}

// ── Status display helpers ──

const statusLabel: Record<string, string> = {
  skipped: 'skipped',
  relevant: 'checked',
  early_exit: 'TRIGGERED',
  filtering: 'FILTERED',
  no_action: 'no effect',
  error: 'ERROR',
};

const statusCssClass: Record<string, string> = {
  skipped: 'stepSkipped',
  relevant: 'stepRelevant',
  early_exit: 'stepTriggered',
  filtering: 'stepFiltered',
  no_action: 'stepNoAction',
  error: 'stepError',
};

export default function MoveExplanationModal({
  moveResult,
  variantLabel,
  moveDescription,
  onClose,
}: MoveExplanationModalProps) {
  const [isLightExpanded, setIsLightExpanded] = useState(false);
  const [isDarkExpanded, setIsDarkExpanded] = useState(false);
  const [isRawLogsExpanded, setIsRawLogsExpanded] = useState(false);

  const breakdown = moveResult.evaluationBreakdown;
  const pv = moveResult.principalVariation || [];
  const decisionLog = moveResult.decisionLog;
  const isPriorityModel = !!decisionLog && decisionLog.length > 0;

  // Parse priority decision log
  const parsed = useMemo(() => {
    if (!decisionLog || decisionLog.length === 0) return null;
    return parseDecisionLog(decisionLog);
  }, [decisionLog]);

  // Parse depth and nodes from reasoning string (minimax)
  const depth = moveResult.reasoning?.match(/Depth (\d+)/)?.[1]
    || moveResult.reasoning?.match(/depth (\d+)/)?.[1] || '?';
  const nodes = moveResult.reasoning?.match(/nodes=(\d+)/)?.[1] || '?';

  // Find highest-priority step that took action
  const triggerStep = parsed?.steps.find(s => s.status === 'early_exit');
  const lastReached = parsed?.steps[parsed.steps.length - 1];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>&times;</button>

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{variantLabel} Analysis</h2>
          <div className={styles.subtitle}>{moveDescription}</div>
          <div className={styles.statRow}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Confidence</span>
              <span className={`${styles.statValue} ${styles.statValueBlue}`}>
                {Math.round(moveResult.confidence * 100)}%
              </span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Think Time</span>
              <span className={styles.statValue}>
                {(moveResult.thinkingTime / 1000).toFixed(1)}s
              </span>
            </div>
            {!isPriorityModel && (
              <>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Depth</span>
                  <span className={styles.statValue}>{depth}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Nodes</span>
                  <span className={styles.statValue}>{Number(nodes).toLocaleString()}</span>
                </div>
              </>
            )}
            {moveResult.evaluation !== undefined && (
              <div className={styles.stat}>
                <span className={styles.statLabel}>Score</span>
                <span className={styles.statValue}>
                  {moveResult.evaluation.toFixed(0)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ═══════ PRIORITY MODEL VIEW ═══════ */}
        {isPriorityModel && parsed && (
          <>
            {/* Decision type summary */}
            <div className={styles.section}>
              <div className={styles.decisionSummary}>
                {triggerStep ? (
                  <span className={styles.decisionType}>
                    Priority {triggerStep.number} Early Exit: <strong>{triggerStep.name}</strong>
                  </span>
                ) : parsed.fallbackType === 'minimax' ? (
                  <span className={styles.decisionType}>
                    Priorities filtered moves \u2192 <strong>Minimax fallback</strong>
                  </span>
                ) : parsed.fallbackType === 'mcts' ? (
                  <span className={styles.decisionType}>
                    Priorities filtered moves \u2192 <strong>MCTS fallback</strong>
                  </span>
                ) : (
                  <span className={styles.decisionType}>
                    No priority triggered \u2192 <strong>Fallback</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Decision chain */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Decision Chain</h3>
              <div className={styles.chainContainer}>
                {parsed.steps.map((step) => (
                  <div key={step.number} className={`${styles.chainStep} ${styles[statusCssClass[step.status]] || ''}`}>
                    <span className={styles.chainNumber}>{step.number}.</span>
                    <span className={styles.chainName}>{step.name}</span>
                    <span className={`${styles.chainStatus} ${styles[statusCssClass[step.status]] || ''}`}>
                      {statusLabel[step.status]}
                    </span>
                    {step.movesFiltered !== undefined && step.movesFiltered > 0 && (
                      <span className={styles.chainMeta}>-{step.movesFiltered} moves</span>
                    )}
                    {step.reasoning && (
                      <div className={styles.chainReasoning}>{step.reasoning}</div>
                    )}
                    {step.details.length > 0 && (
                      <div className={styles.chainDetails}>
                        {step.details.map((d, i) => (
                          <div key={i} className={styles.chainDetail}>{d}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {/* Show "not reached" for remaining priorities */}
                {triggerStep && lastReached && lastReached.number < 29 && (
                  <div className={`${styles.chainStep} ${styles.stepSkipped}`}>
                    <span className={styles.chainNumber}>{lastReached.number + 1}-29.</span>
                    <span className={styles.chainName}>Not reached</span>
                    <span className={`${styles.chainStatus} ${styles.stepSkipped}`}>
                      early exit at P{triggerStep.number}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Minimax fallback section (for Creature) */}
            {parsed.fallbackType === 'minimax' && parsed.postamble.length > 0 && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Minimax Fallback</h3>
                <div className={styles.fallbackInfo}>
                  {parsed.postamble.map((line, i) => (
                    <div key={i} className={styles.fallbackLine}>{line}</div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══════ SHARED: Principal Variation ═══════ */}
        {pv.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Best Line (Principal Variation)</h3>
            <div className={styles.pvContainer}>
              <div className={styles.pvNote}>
                The sequence of moves the AI calculated as optimal for both sides.
                <strong> MV</strong> = value change for the moving player. <strong>AV</strong> = absolute board value (Light - Dark).
              </div>
              {pv.map((node: any, idx: number) => {
                const previousAV = idx === 0
                  ? (moveResult.initialBoardValue || 0)
                  : (pv[idx - 1]?.absoluteValue || 0);
                const currentAV = node.absoluteValue !== undefined ? node.absoluteValue : 0;
                const avDelta = currentAV - previousAV;

                const isLightMove = node.playerTurn === 'Light';
                const playerDelta = isLightMove ? avDelta : -avDelta;
                const isTerminal = Math.abs(currentAV) >= 100000;
                const terminalText = currentAV >= 100000 ? 'LIGHT WINS' : currentAV <= -100000 ? 'DARK WINS' : '';

                return (
                  <div key={idx} className={styles.pvMove}>
                    <span className={styles.pvNumber}>{idx + 1}.</span>
                    <span className={styles.pvDescription}>{node.description}</span>
                    {node.absoluteValue !== undefined && (
                      <>
                        {!isTerminal ? (
                          <>
                            <span className={`${styles.pvDelta} ${playerDelta >= 0 ? styles.pvDeltaPositive : styles.pvDeltaNegative}`}>
                              {node.playerTurn}: {playerDelta >= 0 ? '+' : ''}{playerDelta}
                            </span>
                            <span className={styles.pvAV}>
                              AV: {currentAV >= 0 ? '+' : ''}{currentAV}
                            </span>
                          </>
                        ) : (
                          <span className={styles.pvTerminal}>{terminalText}</span>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════ MINIMAX-ONLY SECTIONS ═══════ */}

        {/* Board Evaluation */}
        {breakdown && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Board Evaluation (After Move)</h3>
            <div className={styles.evalSummary}>
              <div className={styles.evalTeam}>
                <div className={`${styles.evalTeamLabel} ${styles.evalTeamLabelLight}`}>Light</div>
                <div className={styles.evalTeamValue}>{breakdown.lightTotal}</div>
              </div>
              <span className={styles.evalVs}>vs</span>
              <div className={styles.evalTeam}>
                <div className={`${styles.evalTeamLabel} ${styles.evalTeamLabelDark}`}>Dark</div>
                <div className={styles.evalTeamValue}>{breakdown.darkTotal}</div>
              </div>
            </div>
            <div className={styles.evalFinal}>
              Net: {breakdown.finalScore >= 0 ? '+' : ''}{breakdown.finalScore} ({breakdown.finalScore > 0 ? 'Light advantage' : breakdown.finalScore < 0 ? 'Dark advantage' : 'Even'})
            </div>
          </div>
        )}

        {/* Piece Breakdown - Light */}
        {breakdown && (
          <div className={styles.section}>
            <div className={styles.teamHeader} onClick={() => setIsLightExpanded(!isLightExpanded)}>
              <span className={styles.teamArrow}>{isLightExpanded ? '\u25BC' : '\u25B6'}</span>
              <span className={styles.evalTeamLabelLight}>Light Pieces ({breakdown.lightPieces?.length ?? 0})</span>
            </div>
            {isLightExpanded && (
              <div className={styles.pieceList}>
                {(breakdown.lightPieces || []).map((piece: any, idx: number) => (
                  <div key={idx} className={styles.pieceRow}>
                    <span className={styles.pieceName}>
                      {piece.piece}{piece.position} {piece.isKing ? '\u{1F451}' : ''}
                    </span>
                    <span className={styles.pieceBreakdown}>{piece.breakdown}</span>
                    <span className={styles.pieceScore}>= {piece.score}</span>
                  </div>
                ))}
                <div className={styles.teamTotal}>Total: +{breakdown.lightTotal}</div>
              </div>
            )}
          </div>
        )}

        {/* Piece Breakdown - Dark */}
        {breakdown && (
          <div className={styles.section}>
            <div className={styles.teamHeader} onClick={() => setIsDarkExpanded(!isDarkExpanded)}>
              <span className={styles.teamArrow}>{isDarkExpanded ? '\u25BC' : '\u25B6'}</span>
              <span className={styles.evalTeamLabelDark}>Dark Pieces ({breakdown.darkPieces?.length ?? 0})</span>
            </div>
            {isDarkExpanded && (
              <div className={styles.pieceList}>
                {(breakdown.darkPieces || []).map((piece: any, idx: number) => (
                  <div key={idx} className={styles.pieceRow}>
                    <span className={styles.pieceName}>
                      {piece.piece}{piece.position} {piece.isKing ? '\u{1F451}' : ''}
                    </span>
                    <span className={styles.pieceBreakdown}>{piece.breakdown}</span>
                    <span className={styles.pieceScore}>= {piece.score}</span>
                  </div>
                ))}
                <div className={styles.teamTotal}>Total: +{breakdown.darkTotal}</div>
              </div>
            )}
          </div>
        )}

        {/* Scoring Legend (minimax only) */}
        {breakdown && (
          <div className={styles.section}>
            <div className={styles.legend}>
              <span className={styles.legendHighlight}>Sentinel Scoring:</span>{' '}
              Piece (+10), King (+1000), Position (+2 to mid, +1 past), Frog+Lily (+5), Snake+Shed (-3), Entangled (-5), Poisoned (-2).
              Terminal: Win/Loss = {'\u00B1'}100,000
            </div>
          </div>
        )}

        {/* ═══════ RAW LOGS (priority models) ═══════ */}
        {isPriorityModel && decisionLog && (
          <div className={styles.section}>
            <div className={styles.teamHeader} onClick={() => setIsRawLogsExpanded(!isRawLogsExpanded)}>
              <span className={styles.teamArrow}>{isRawLogsExpanded ? '\u25BC' : '\u25B6'}</span>
              <span>Raw Decision Logs ({decisionLog.length} entries)</span>
            </div>
            {isRawLogsExpanded && (
              <div className={styles.rawLogs}>
                {decisionLog.map((line, i) => (
                  <div key={i} className={styles.rawLogLine}>{line}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
